import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React__default__default, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes__default from 'prop-types';
import styles from './autocomplete.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import ReactUtils from '../../packages/react-utils/react-utils.js';
import useComponentId from '../../hooks/use-component-id/use-component-id.js';
import { autocompleteChildrenValidator } from './autocomplete-children-validator.js';
import { AutocompleteOptionPropTypes } from './quarks/autocomplete-option.js';
import { AutocompleteGroupPropTypes } from './quarks/autocomplete-option-group.js';
import { AutocompleteContext, AutocompleteInputContext, AutocompleteOptionsContext, AutocompleteSelectedOptionsContext } from './autocomplete-context.js';
import { Injecss } from '../../packages/injecss/injecss.js';

// init css class
var bem = HoneyBem.init('autocomplete', styles);
var Autocomplete = function (_a) {
  var children = _a.children,
    options = _a.options,
    _b = _a.debounce,
    debounce = _b === void 0 ? 300 : _b,
    _c = _a.extraClasses,
    extraClasses = _c === void 0 ? '' : _c,
    _d = _a.onChange,
    onChange = _d === void 0 ? function () {
      return null;
    } : _d,
    _e = _a.onDebounce,
    onDebounce = _e === void 0 ? function () {
      return null;
    } : _e,
    _f = _a.onDelete,
    onDelete = _f === void 0 ? function () {
      return null;
    } : _f,
    _g = _a.onReset,
    onReset = _g === void 0 ? function () {
      return null;
    } : _g,
    _h = _a.onSelect,
    onSelect = _h === void 0 ? function () {
      return null;
    } : _h,
    _j = _a.optionsSelected,
    optionsSelected = _j === void 0 ? [] : _j,
    _k = _a.value,
    value = _k === void 0 ? '' : _k,
    rest = __rest(_a, ["children", "options", "debounce", "extraClasses", "onChange", "onDebounce", "onDelete", "onReset", "onSelect", "optionsSelected", "value"]);
  // state setters
  // store timeout for debounce
  var _l = useState(null),
    timer = _l[0],
    setTimer = _l[1];
  // input value proxy (to allow overwriting)
  var _m = useState(value),
    inputValue = _m[0],
    setInputValue = _m[1];
  // controls option listbox visibility
  var _o = useState(options && options.length >= 1),
    optionsListVisible = _o[0],
    setOptionsListVisible = _o[1];
  // holds visible option items
  var _p = useState(options && options.map(function (option) {
      return 'options' in option ? option.options.map(function (entry) {
        return entry;
      }) : option;
    }).flat()),
    focusableOptions = _p[0],
    setFocusableOptions = _p[1];
  // stores focused option index allowing for focus control with arrow keys
  var _q = useState(-1),
    focusedOption = _q[0],
    setFocusedOption = _q[1];
  var containerRef = useRef(null);
  var listBoxRef = useRef(null);
  var containerId = useComponentId('hcr-autocomplete');
  var isMultiselect = ReactUtils.getChildrenByName(children, 'AutocompleteSelectedOptions')[0] !== undefined;
  /**
   * Proxies onchange on input (for debounce)
   * fires `onChange` programmatically
   * @param event
   */
  var handleInputChange = function (event) {
    event.persist();
    var val = event.target.value;
    onChange(event);
    setInputValue(val);
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(window.setTimeout(function () {
      return onDebounce && typeof onDebounce === 'function' && onDebounce(event);
    }, debounce));
  };
  /**
   * Runs on option selection
   * Proxies selected option to onSelect callback,
   * Updates focused option index
   * @param selectedOption
   * @param index
   */
  var onOptionSelect = function (selectedOption, index) {
    onSelect(selectedOption);
    // if the list stays visible after the option selection we want to keep track of focused item
    setFocusedOption(index);
    // clearing the input after selection for multiselect mode
    if (isMultiselect) {
      setInputValue('');
    }
  };
  // we don't want to hide the list if there is still an element inside of Autocomplete focused
  var handleComponentBlur = function (event) {
    var origin = event.relatedTarget;
    if (origin && containerRef.current && containerRef.current.contains(origin)) {
      return;
    }
    setOptionsListVisible(false);
  };
  /**
   * Key events handling,
   * mostly used for arrow based focus tracking and "Esc" handling
   * @param event
   */
  var handleKeyDown = function (event) {
    // disabling page scroll when focused on the input
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      // changing visual focus state of options depending on the arrow key press
      if (optionsListVisible && focusableOptions && (listBoxRef === null || listBoxRef === void 0 ? void 0 : listBoxRef.current)) {
        if (event.key === 'ArrowUp') {
          var nextInteractiveOptionIndex = focusedOption <= 0 ? focusableOptions.length - 1 : focusedOption - 1;
          setFocusedOption(nextInteractiveOptionIndex);
        } else if (event.key === 'ArrowDown') {
          var nextInteractiveOptionIndex = focusedOption < focusableOptions.length - 1 ? focusedOption + 1 : 0;
          // jumping to the start if end is reached
          setFocusedOption(nextInteractiveOptionIndex);
        }
      }
    }
    // force closing options list, triggers focus back on the input
    if (event.key === 'Escape') {
      setOptionsListVisible(false);
    }
    // perform selection
    if (event.key === 'Enter' && options && focusedOption >= 0) {
      onOptionSelect(focusableOptions[focusedOption], focusedOption);
    }
  };
  // overwrite controlled value if prop value changes
  // (allows setting value arbitrarily)
  useEffect(function () {
    setInputValue(value);
  }, [value]);
  useEffect(function () {
    // force setting list to visible if any options has been supplied
    setOptionsListVisible(options && options.length >= 1);
    // update list of focusable options
    setFocusableOptions(options && options.map(function (option) {
      return 'options' in option ? option.options.map(function (entry) {
        return entry;
      }) : option;
    }).flat());
  }, [options]);
  // reset focus tracking on option list re-render
  useEffect(function () {
    setFocusedOption(-1);
  }, [optionsListVisible]);
  var autocompleteState = useMemo(function () {
    return {
      containerId: containerId,
      onDelete: onDelete,
      onReset: onReset,
      focusableOptions: focusableOptions,
      selectedOptions: optionsSelected,
      focusedOption: focusedOption,
      isMultiselect: isMultiselect
    };
  }, [containerId, onDelete, onReset, focusableOptions, optionsSelected, focusedOption, isMultiselect]);
  var autocompleteInputState = useMemo(function () {
    return {
      optionsListVisible: optionsListVisible,
      onChange: handleInputChange,
      value: inputValue
    };
  }, [optionsListVisible, handleInputChange, value]);
  var autocompleteOptionsState = useMemo(function () {
    return {
      options: options,
      listBoxRef: listBoxRef,
      onOptionSelect: onOptionSelect
    };
  }, [options, listBoxRef, onOptionSelect]);
  var autocompleteSelectedOptionsState = useMemo(function () {
    return {
      onDelete: onDelete,
      onReset: onReset
    };
  }, [onDelete, onReset]);
  return /*#__PURE__*/React__default__default.createElement(AutocompleteContext.Provider, {
    value: autocompleteState
  }, /*#__PURE__*/React__default__default.createElement("div", __assign({
    className: bem.b(false, extraClasses),
    ref: containerRef,
    onBlur: handleComponentBlur,
    // @todo improve blur and outside click handling
    role: "presentation",
    // @todo tabIndex may cause accessibility issues, focusing on unlabelled container.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    tabIndex: 0,
    onKeyDown: function (event) {
      handleKeyDown(event);
    }
  }, rest), /*#__PURE__*/React__default__default.createElement(AutocompleteInputContext.Provider, {
    value: autocompleteInputState
  }, ReactUtils.getChildrenByName(children, 'AutocompleteInput')[0]), optionsListVisible && options && options.length >= 1 && /*#__PURE__*/React__default__default.createElement(AutocompleteOptionsContext.Provider, {
    value: autocompleteOptionsState
  }, ReactUtils.getChildrenByName(children, 'AutocompleteOptions')[0]), optionsSelected && optionsSelected.length >= 1 && /*#__PURE__*/React__default__default.createElement(AutocompleteSelectedOptionsContext.Provider, {
    value: autocompleteSelectedOptionsState
  }, ReactUtils.getChildrenByName(children, 'AutocompleteSelectedOptions')[0])));
};
Autocomplete.displayName = 'Autocomplete';
Autocomplete.propTypes = {
  children: autocompleteChildrenValidator(),
  debounce: PropTypes__default.number,
  extraClasses: PropTypes__default.string,
  onChange: PropTypes__default.func,
  onDebounce: PropTypes__default.func,
  onDelete: PropTypes__default.func,
  onReset: PropTypes__default.func,
  onSelect: PropTypes__default.func,
  options: PropTypes__default.oneOfType([PropTypes__default.arrayOf(AutocompleteOptionPropTypes), PropTypes__default.arrayOf(AutocompleteGroupPropTypes)]).isRequired,
  optionsSelected: PropTypes__default.arrayOf(AutocompleteOptionPropTypes),
  value: PropTypes__default.oneOfType([PropTypes__default.string, PropTypes__default.array])
};
var autocomplete = Injecss(Autocomplete, styles);

export { Autocomplete, autocomplete as default };
